import { ButtonContainer, IDataTableColumn } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import {
  EBooleanSearchTypes,
  SiegeLieuSuiviGridDto,
  SiegeLieuSuiviSearchGlobal,
  SuiviSiegeLieuApi
} from "../../../../api";
import { ERoutes } from "../../../../AppRouter";
import { EditButton, LinkButton, SearchTablePage, ViewButton } from "../../../../components";
import { useAuth } from "../../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface ISuiviSiegeLieuSearchPageProps {}

export const SuiviSiegeLieuSearchPage: React.FunctionComponent<ISuiviSiegeLieuSearchPageProps> = props => {
  const { t, tUnsafe } = useTl();
  const history = useHistory();
  const api = useApiService(SuiviSiegeLieuApi);
  const { user } = useAuth();

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: SiegeLieuSuiviGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => {
                const path = !!row.idlieuFormation
                  ? `${ERoutes.lieuFormation}/${row.idlieuFormation}/suivi/view/${row.idsuivi}?idsiegeSocial=${row.idsiegeSocial}`
                  : `${ERoutes.siegeSocial}/${row.idsiegeSocial}/suivi/view/${row.idsuivi}`;
                history.push(path);
              }}
            />

            {row.idrealisateurSuivi === user.iduser && (
              <EditButton
                minimal={true}
                onClick={() => {
                  const path = !!row.idlieuFormation
                    ? `${ERoutes.lieuFormation}/${row.idlieuFormation}/suivi/edit/${row.idsuivi}?idsiegeSocial=${row.idsiegeSocial}`
                    : `${ERoutes.siegeSocial}/${row.idsiegeSocial}/suivi/edit/${row.idsuivi}`;
                  history.push(path);
                }}
              />
            )}
          </ButtonContainer>
        )
      },
      {
        header: () => t(ETLCodes.DateSuivi),
        fieldName: "dateSuivi"
      },
      {
        header: () => t(ETLCodes.RealisateurSuivi),
        fieldName: "realisateurSuivi",
        render: (row: SiegeLieuSuiviGridDto) => (
          <LinkButton
            text={row.realisateurSuivi}
            onClick={() => window.open(`#${ERoutes.users}/${row.idrealisateurSuivi}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.Encodeur),
        fieldName: "encodeur",
        render: (row: SiegeLieuSuiviGridDto) => (
          <LinkButton
            text={row.encodeur}
            onClick={() => window.open(`#${ERoutes.users}/${row.idencodeur}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.TypeSuivi),
        fieldName: "typeSuivi"
      },
      {
        header: () => t(ETLCodes.ContenuCache),
        fieldName: "hiddenContent"
      }
    ],
    [history, t, user.iduser]
  );

  const getCriteriasFunc = React.useCallback(async () => {
    let criterias = await api.suiviSiegeLieuGetSearchCriterias({ includeListsValues: true });
    criterias = criterias.map(c => {
      if (c.criteria === "SuiviAction") {
        c.listValues.forEach(v => (v.displayValue = tUnsafe("SuiviAction_" + v.displayValue)));
      }
      return c;
    });
    return criterias;
  }, [api, tUnsafe]);

  const apiFactory = useAbortableApiServiceFactory(SuiviSiegeLieuApi);
  const lastAbortController = React.useRef<AbortController>();
  const searchFunc = React.useCallback(
    (nextSearch?: SiegeLieuSuiviSearchGlobal) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.suiviSiegeLieuBaseSearch({ SiegeLieuSuiviSearchGlobal: { ...nextSearch } });
    },
    [apiFactory]
  );

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={getCriteriasFunc}
      searchFunction={searchFunc}
      onAbort={onAbort}
      breadCrumbs={[{ text: t(ETLCodes.SuivisSiegesLieux) }]}
      sortKeys={{ dateSuivi: "DESC", encodeur: "ASC" }}
      searchStateInitialSearch={false}
      defaultCriterias={[
        {
          criteria: "onlyUserSuivis",
          searchMode: EBooleanSearchTypes.Equals,
          value: true
        }
      ]}
    />
  );
};
